// tilemap.ts ↔ game/tilemap.cpp — the terrain tile map.
//
// A level names a .tmap (header { szFnTset[28]; ctx, cty BE } + ctx*cty BE words
// of tile OFFSETS) whose tile pixels live in .tset records ("desert.tset",
// ".tset.1", … — header { cTiles, cxTile, cyTile } BE, kcbTileSetHeader=6, then
// cTiles raw 32-bit tiles). Tile dwords are RGBA8888 in SDL mask terms
// (R=0xff000000): little-endian memory bytes [A,B,G,R] → converted here to the
// RGBA byte order every other surface uses (bitmap.cpp:117 Init(dword*)).
// Draw composites visible, unfogged, invalidated tiles into an internal draw
// surface at the scroll remainder, then blits that surface to the target.

import { CpuDibBitmap } from './dibbitmap.ts';
import type { PixelSource } from './dibbitmap.ts';
import type { PackFileReader } from '../io/packfile.ts';

export const kcbTileSetHeader = 6; // ht.h:1298
const kcchFnTset = 28; // ht.h:1308

/** The UpdateMap surface TileMap::Draw consumes (updatemap.cpp ports in a later
 *  slice): per-tile invalidation plus the playfield tiling geometry. */
export interface UpdateMapLike {
  GetMapInfo(): { cxLeftTile: number; cyTopTile: number };
  GetInvalidMap(): Uint8Array; // m_ctx*m_cty flags, row-major
  EnumUpdateRects(
    fFirst: boolean,
    prcBounds: { left: number; top: number; right: number; bottom: number } | null,
  ): { left: number; top: number; right: number; bottom: number } | null;
}

export interface FogMapLike {
  isFogOpaque(iCell: number): boolean;
}

// ht.h IsFogOpaque: kbOpaque == 0 in the low nibble
export const isFogOpaqueByte = (b: number): boolean => (b & 0x0f) === 0;

export function TcFromPc(pc: number, cTilePc: number): number {
  return Math.floor(pc / cTilePc);
}

export class TileMap {
  m_ctx = 0; // draw-map tile columns (playfield + 1)
  m_cty = 0;
  m_cxTile = 0;
  m_cyTile = 0;
  m_cTiles = 0;
  m_tiles: PixelSource[] = [];
  m_pwMapData: Uint16Array | null = null; // BE-decoded tile offsets
  m_ctxMap = 0; // map tile columns (tmap header)
  m_ctyMap = 0;
  m_pbmDraw: CpuDibBitmap | null = null;
  m_miniTset: Uint8Array | null = null;
  /** Renderer seam: the draw-surface factory. The C++ m_pbmDraw is a GPU
   *  target texture (tilemap.cpp via CreateDibBitmap); under the WebGL2
   *  renderer this creates a GL surface. Default = the CPU reference. */
  private createSurface: (cx: number, cy: number) => CpuDibBitmap = (cx, cy) =>
    new CpuDibBitmap(cx, cy);

  // game/tilemap.cpp:49-144
  Load(
    pakr: PackFileReader,
    psz: string,
    sizPlayfield: { cx: number; cy: number },
    createSurface?: (cx: number, cy: number) => CpuDibBitmap,
  ): boolean {
    if (createSurface !== undefined) this.createSurface = createSurface;
    const tmap = pakr.MapFile(psz);
    if (tmap === null) return false;

    let szFnTset = '';
    for (let i = 0; i < kcchFnTset && tmap[i] !== 0; i++) szFnTset += String.fromCharCode(tmap[i]);
    this.m_ctxMap = (tmap[kcchFnTset] << 8) | tmap[kcchFnTset + 1];
    this.m_ctyMap = (tmap[kcchFnTset + 2] << 8) | tmap[kcchFnTset + 3];

    const cCells = this.m_ctxMap * this.m_ctyMap;
    this.m_pwMapData = new Uint16Array(cCells);
    const base = kcchFnTset + 4;
    for (let i = 0; i < cCells; i++) {
      this.m_pwMapData[i] = (tmap[base + i * 2] << 8) | tmap[base + i * 2 + 1];
    }

    // tsets: name, name.1, name.2, ... until missing (tilemap.cpp:69-90)
    this.m_cTiles = 0;
    this.m_tiles = [];
    for (let nTset = 0; ; nTset++) {
      const name = nTset === 0 ? szFnTset : `${szFnTset}.${nTset}`;
      const tset = pakr.MapFile(name);
      if (tset === null) break;
      // TileSetHeader field order: cTiles FIRST (ht.h:1292-1297)
      const cTiles = (tset[0] << 8) | tset[1];
      const cxTile = (tset[2] << 8) | tset[3];
      const cyTile = (tset[4] << 8) | tset[5];
      this.m_cxTile = cxTile;
      this.m_cyTile = cyTile;
      // raw 32-bit tiles: LE dword with R in the high byte -> bytes [A,B,G,R]
      const cbTile = cxTile * cyTile * 4;
      for (let nTile = 0; nTile < cTiles; nTile++) {
        const src = tset.subarray(
          kcbTileSetHeader + nTile * cbTile,
          kcbTileSetHeader + (nTile + 1) * cbTile,
        );
        const rgba = new Uint8Array(cbTile);
        for (let i = 0; i < cbTile; i += 4) {
          rgba[i] = src[i + 3];
          rgba[i + 1] = src[i + 2];
          rgba[i + 2] = src[i + 1];
          rgba[i + 3] = src[i];
        }
        this.m_tiles.push({ cx: cxTile, cy: cyTile, rgba });
        this.m_cTiles++;
      }
    }
    if (this.m_cTiles === 0) return false;

    if (!this.SetPlayfieldSize(sizPlayfield)) return false;

    // mini tset (minimap scales) — mapped raw; consumed by the minimap port
    this.m_miniTset = pakr.MapFile(`${szFnTset}mini`);
    return this.m_miniTset !== null;
  }

  // game/tilemap.cpp:146-156
  SetPlayfieldSize(sizPlayfield: { cx: number; cy: number }): boolean {
    this.m_ctx = Math.floor((sizPlayfield.cx + (this.m_cxTile - 1)) / this.m_cxTile) + 1;
    this.m_cty = Math.floor((sizPlayfield.cy + (this.m_cyTile - 1)) / this.m_cyTile) + 1;
    (this.m_pbmDraw as { dispose?: () => void } | undefined)?.dispose?.(); // free the old GL surface (resize)
    this.m_pbmDraw = this.createSurface(sizPlayfield.cx, sizPlayfield.cy);
    return true;
  }

  /** Free the GL draw surface deterministically (the FinalizationRegistry is too lazy for off-heap GL
   *  memory). Called when a GameRenderer is discarded (replay-viewer rebuilds one per backward seek). */
  dispose(): void {
    (this.m_pbmDraw as { dispose?: () => void } | undefined)?.dispose?.();
  }

  // game/tilemap.cpp:158-168 — walk the chained mini-tset headers to a scale
  GetMiniTileSetHeader(nScale: number): { cxTile: number; cyTile: number; off: number } | null {
    if (this.m_miniTset === null) return null;
    let off = 0;
    for (;;) {
      // MiniTileSetHeader: { offNext, cTiles, cxTile, cyTile } (ht.h:1300-1306)
      const offNext = (this.m_miniTset[off] << 8) | this.m_miniTset[off + 1];
      const cxTile = (this.m_miniTset[off + 4] << 8) | this.m_miniTset[off + 5];
      const cyTile = (this.m_miniTset[off + 6] << 8) | this.m_miniTset[off + 7];
      if (cxTile === nScale) return { cxTile, cyTile, off };
      if (offNext === 0) return null;
      off += offNext;
    }
  }

  // game/tilemap.cpp:170-180
  GetMapSize(): { cx: number; cy: number } {
    return { cx: this.m_ctxMap * this.m_cxTile, cy: this.m_ctyMap * this.m_cyTile };
  }

  GetTCoordMapSize(): { cx: number; cy: number } {
    // ht.h inline
    return { cx: this.m_ctxMap, cy: this.m_ctyMap };
  }

  GetTileSize(): { cx: number; cy: number } {
    return { cx: this.m_cxTile, cy: this.m_cyTile };
  }

  // game/tilemap.cpp:182-286
  Draw(
    pbm: CpuDibBitmap,
    x: number,
    y: number,
    cx: number,
    cy: number,
    xMap: number,
    yMap: number,
    pbFogMap: Uint8Array,
    pupd: UpdateMapLike,
  ): void {
    const tx = TcFromPc(xMap, this.m_cxTile);
    const ty = TcFromPc(yMap, this.m_cyTile);

    const pmnfo = pupd.GetMapInfo();
    let ctx =
      TcFromPc(cx - pmnfo.cxLeftTile + this.m_cxTile - 1, this.m_cxTile) +
      TcFromPc(pmnfo.cxLeftTile + this.m_cxTile - 1, this.m_cxTile);
    let cty =
      TcFromPc(cy - pmnfo.cyTopTile + this.m_cyTile - 1, this.m_cyTile) +
      TcFromPc(pmnfo.cyTopTile + this.m_cyTile - 1, this.m_cyTile);
    const ctxMap = this.m_ctxMap;

    const sizMap = this.GetMapSize();
    if (sizMap.cx < cx) ctx = TcFromPc(sizMap.cx, this.m_cxTile);
    if (sizMap.cy < cy) cty = TcFromPc(sizMap.cy, this.m_cyTile);

    const iCell = ty * ctxMap + tx;
    let iwMap = iCell;
    let ibFog = iCell;
    const invalid = pupd.GetInvalidMap();
    let iInvalid = 0;

    const cReturnDrawMap = this.m_ctx - ctx;
    const cReturnTileMap = ctxMap - ctx;
    for (let tyT = 0; tyT < cty; tyT++) {
      for (let txT = 0; txT < ctx; txT++) {
        if (!isFogOpaqueByte(pbFogMap[ibFog])) {
          if (invalid[iInvalid]) {
            const offset = this.m_pwMapData![iwMap];
            if (offset < 0xff00) {
              this.m_pbmDraw!.Blt(
                this.m_tiles[offset >> 2],
                null,
                this.m_cxTile * txT - (xMap % this.m_cxTile),
                this.m_cyTile * tyT - (yMap % this.m_cyTile),
              );
            }
          }
        }
        ibFog++;
        iInvalid++;
        iwMap++;
      }
      iInvalid += cReturnDrawMap;
      iwMap += cReturnTileMap;
      ibFog += cReturnTileMap;
    }

    // Composite ONLY the update-map-invalid regions of the draw surface (the Palm-era
    // DrawTileMapThunk arm, tilemap.cpp's #else). The SDL-era `pbm->Blt(m_pbmDraw, NULL)`
    // full composite stomped every VALID tile's fog/gob pixels with bare terrain — hidden
    // while displayFrameStart force-invalidated the whole frame, but a real stale-pixel
    // bug under damage-driven scoped invalidation (the fog overlay only repaints invalid
    // tiles, so a full composite left fogless terrain behind). BltTiles carries only a
    // y offset; every caller draws at x=0 (drawBackground + the editor background arm).
    pbm.BltTiles(this.m_pbmDraw!, pupd, y);
  }
}

// game/tilemap.cpp:5-13
export function LoadTileMap(
  pakr: PackFileReader,
  pszFn: string,
  sizPlayfield: { cx: number; cy: number },
  createSurface?: (cx: number, cy: number) => CpuDibBitmap,
): TileMap | null {
  const ptmap = new TileMap();
  return ptmap.Load(pakr, pszFn, sizPlayfield, createSurface) ? ptmap : null;
}
